import { useState, useEffect } from "react";
import NextLink from "next/link";
import { useSession } from "next-auth/client";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Link,
  Text,
  Flex,
  Center,
} from "@chakra-ui/react";
import { ImStatsBars } from "react-icons/im";
import toast from "react-hot-toast";
import PuffLoader from "react-spinners/PuffLoader";
import Thread from "components/tweets/masonry/thread";
import { useFirebaseUser } from "utils/useFirebaseUser";
import { firebaseClient } from "firebaseClient";
import { getAccount } from "utils/sessionHelper";
import { roundUp } from "utils/helpers";
import { variant } from "theme/names";

interface Props {
  tweet: any;
  isOpen: boolean;
  onClose: () => void;
}

export function StatsPopup({ tweet, isOpen, onClose }: Props) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} returnFocusOnClose={false}>
      <ModalOverlay />
      <ModalContent pb="3">
        <ModalHeader>Statistics for this tweet</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <StatsPopupBody tweet={tweet} />
        </ModalBody>

        <ModalFooter justifyContent="center">
          <Button variant="tertiary" onClick={onClose} mr={3}>Cancel</Button>
          <NextLink href="/stats" passHref legacyBehavior>
            <Button
              as={Link}
              // colorScheme="yellow"
              // color="white"
              // borderRadius="3xl"
              variant={variant.Button.primaryWarning}
              leftIcon={<ImStatsBars />}
              _hover={{
                textDecor: "none",
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              Analytics
            </Button>
          </NextLink>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

interface StatsPopupBodyProps {
  tweet: any;
}

function StatsPopupBody({ tweet }: StatsPopupBodyProps) {
  const [stats, setStats] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const isAuthenticated = useFirebaseUser();
  const [session] = useSession();
  const threadTweets = tweet.text.split("\n\n\n");

  console.log("tweet: ", tweet);

  const getStats = async () => {
    try {
      setIsLoading(true);
      const db = firebaseClient.firestore();
      const doc = await db
        .collection("users")
        .doc(getAccount(session)?.id)
        .collection("accounts")
        .doc(getAccount(session).idAccount)
        .collection("tweetsStats")
        .doc(tweet.id_str)
        .get();

      setStats(doc.data());
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      console.log("There was a problem in loading stats: ", err);
      toast.error("There was a problem in loading stats: " + err.message);
    }
  };

  useEffect(() => {
    if (isAuthenticated && !hasFetched) {
      setHasFetched(true);
      getStats();
    }
  }, [isAuthenticated]);

  if (!threadTweets) {
    return null;
  }

  return (
    <Box>
      <Flex justifyContent="center">
        <Thread
          threadTweets={threadTweets}
          disableNbChar={true}
          tweet={tweet}
          showOutline={true}
          disablePreview={tweet?.isPreventLinkExpand}
        />
      </Flex>
      {isLoading ? (
        <Center pt={10}>
          <PuffLoader
            color={"hsl(203, 100%, 36%)"}
            loading={isLoading}
            css="display: block; margin: 0 auto; border-color: red;"
            size={50}
          />
        </Center>
      ) : stats ? (
        <StatsUI stats={stats} />
      ) : (
        <Text textAlign="center" mt="5">
          <i>No stats found.</i> 😔
        </Text>
      )}
    </Box>
  );
}

interface StatsUIProps {
  stats: any;
}

function StatsUI({ stats }: StatsUIProps) {
  const engagementPercent =
    (stats.public_metrics.total_engagement * 100) /
    stats.non_public_metrics.impression_count;

  return (
    <Box my="5" fontWeight="bold">
      <Flex justifyContent="space-evenly">
        <Box>
          <Text fontSize="lg">
            {roundUp(stats.non_public_metrics.impression_count)}
          </Text>
          <Text color="gray">IMPRESSIONS</Text>
        </Box>
        <Box>
          <Text fontSize="lg">{engagementPercent.toFixed(2)}%</Text>
          <Text color="gray">ENGAGEMENT (%)</Text>
        </Box>
      </Flex>
      <Flex justifyContent="space-evenly" mt="8">
        <Box>
          <Text fontSize="lg">{roundUp(stats.public_metrics.like_count)}</Text>
          <Text color="gray">LIKES</Text>
        </Box>
        <Box>
          <Text fontSize="lg">
            {roundUp(stats.public_metrics.retweet_count)}
          </Text>
          <Text color="gray">RTs</Text>
        </Box>
        <Box>
          <Text fontSize="lg">{roundUp(stats.public_metrics.quote_count)}</Text>
          <Text color="gray">
            QUOTE
            <br />
            RTs
          </Text>
        </Box>
        <Box>
          <Text fontSize="lg">
            {roundUp(stats.non_public_metrics.user_profile_clicks)}
          </Text>
          <Text color="gray">
            PROFILE
            <br />
            VISITS
          </Text>
        </Box>
      </Flex>
    </Box>
  );
}
